import React, { useState } from "react";
import Papa from "papaparse";
import { Upload, Download, AlertTriangle, CheckCircle, FileDown } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Required fields are denoted with a trailing * in the *template* header for
// human readability; the parser strips the * before sending to the backend.
const TEMPLATE_HEADERS = [
  "employee_id*", "first_name*", "last_name*", "email*", "role*",
  "phone", "date_of_birth", "gender", "nationality", "personal_email",
  "address_line1", "address_line2", "city", "state", "postal_code", "country",
  "title", "department", "location", "employment_type", "start_date", "end_date",
  "manager_email", "work_location", "probation_end_date",
  "salary_band", "currency",
  "emergency_contact_name", "emergency_contact_phone", "emergency_contact_relationship",
];

const REQUIRED = ["employee_id", "first_name", "last_name", "email", "role"];

const TEMPLATE_EXAMPLE = {
  employee_id: "EMP001", first_name: "John", last_name: "Smith",
  email: "john.smith@acme.com", role: "staff",
  phone: "+44 20 7946 0001", date_of_birth: "1990-04-22",
  gender: "male", nationality: "British", personal_email: "john@personal.com",
  address_line1: "10 Downing Street", address_line2: "Flat 2",
  city: "London", state: "Greater London", postal_code: "SW1A 2AA", country: "United Kingdom",
  title: "Software Engineer", department: "Engineering", location: "London HQ",
  employment_type: "full_time", start_date: "2025-01-15", end_date: "",
  manager_email: "manager@acme.com", work_location: "hybrid", probation_end_date: "2025-07-15",
  salary_band: "Band B", currency: "GBP",
  emergency_contact_name: "Jane Smith", emergency_contact_phone: "+44 20 7946 0099",
  emergency_contact_relationship: "Spouse",
};

const TEMPLATE_HINTS = {
  employee_id: "Unique within org",
  first_name: "Max 100 chars",
  last_name: "Max 100 chars",
  email: "Valid email, unique per org",
  role: "staff/manager/admin/executive",
  phone: "Any format",
  date_of_birth: "YYYY-MM-DD",
  gender: "male/female/other/prefer_not_to_say",
  nationality: "",
  personal_email: "Valid email format",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  title: "",
  department: "",
  location: "",
  employment_type: "full_time/part_time/contract/intern",
  start_date: "YYYY-MM-DD",
  end_date: "YYYY-MM-DD",
  manager_email: "Must match a user email in same org",
  work_location: "office/remote/hybrid",
  probation_end_date: "YYYY-MM-DD",
  salary_band: "Band A/Band B/Band C/Band D",
  currency: "GBP/USD/EUR/INR/AED",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relationship: "",
};

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const VALID_ROLES = ["staff", "manager", "admin", "executive"];
const VALID_GENDER = ["male", "female", "other", "prefer_not_to_say"];
const VALID_EMPL = ["full_time", "part_time", "contract", "intern"];
const VALID_WORK = ["office", "remote", "hybrid"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE  = /^\d{4}-\d{2}-\d{2}$/;

// Strip trailing `*` from template headers so users keep them or remove them
const normalizeHeader = (h) => String(h || "").trim().toLowerCase().replace(/\*$/, "");

function validateRow(row, rowNum, seenIds, seenEmails) {
  const errs = [];
  const must = (k, label) => {
    if (!String(row[k] || "").trim()) errs.push({ field: k, error: "Required field missing" });
  };
  REQUIRED.forEach((k) => must(k));

  const email = String(row.email || "").trim().toLowerCase();
  if (email && !EMAIL_RE.test(email)) errs.push({ field: "email", error: "Invalid email format" });

  const role = String(row.role || "").trim().toLowerCase();
  if (role && !VALID_ROLES.includes(role)) errs.push({ field: "role", error: "Must be one of: " + VALID_ROLES.join("/") });

  ["date_of_birth", "start_date", "end_date", "probation_end_date"].forEach((k) => {
    const v = String(row[k] || "").trim();
    if (v && !DATE_RE.test(v)) errs.push({ field: k, error: "Must be YYYY-MM-DD" });
  });

  const pe = String(row.personal_email || "").trim();
  if (pe && !EMAIL_RE.test(pe)) errs.push({ field: "personal_email", error: "Invalid email format" });
  const me = String(row.manager_email || "").trim();
  if (me && !EMAIL_RE.test(me)) errs.push({ field: "manager_email", error: "Invalid email format" });

  const g = String(row.gender || "").trim().toLowerCase();
  if (g && !VALID_GENDER.includes(g)) errs.push({ field: "gender", error: "Must be one of: " + VALID_GENDER.join("/") });
  const et = String(row.employment_type || "").trim().toLowerCase().replace(/-/g, "_");
  if (et && !VALID_EMPL.includes(et)) errs.push({ field: "employment_type", error: "Must be one of: " + VALID_EMPL.join("/") });
  const wl = String(row.work_location || "").trim().toLowerCase();
  if (wl && !VALID_WORK.includes(wl)) errs.push({ field: "work_location", error: "Must be one of: " + VALID_WORK.join("/") });

  // Client-side dup check across the file (server still re-checks per org)
  const eid = String(row.employee_id || "").trim();
  if (eid && seenIds.has(eid)) errs.push({ field: "employee_id", error: "Duplicate within file" });
  if (eid) seenIds.add(eid);
  if (email && seenEmails.has(email)) errs.push({ field: "email", error: "Duplicate within file" });
  if (email) seenEmails.add(email);

  return errs;
}

function buildTemplateCsv() {
  // Three rows: headers (with *), example, format hints
  const cleanKeys = TEMPLATE_HEADERS.map(normalizeHeader);
  const exampleRow = cleanKeys.map((k) => TEMPLATE_EXAMPLE[k] ?? "");
  const hintRow = cleanKeys.map((k) => TEMPLATE_HINTS[k] ?? "");
  // Use Papa.unparse with explicit rows so we get the * in the visible header
  return Papa.unparse({
    fields: TEMPLATE_HEADERS,
    data: [exampleRow, hintRow],
  });
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function BulkUserUpload({ onUploaded }) {
  const [rows, setRows]             = useState([]);   // valid + invalid combined
  const [clientErrors, setClientErrors] = useState([]); // [{row, employee_id, email, errors}]
  const [uploading, setUploading]   = useState(false);
  const [msg, setMsg]               = useState(null);
  const [fileName, setFileName]     = useState(null);
  const [serverResult, setServerResult] = useState(null);  // {summary, results}

  const downloadTemplate = () => {
    downloadCsv("users_template.csv", buildTemplateCsv());
  };

  const onFile = (file) => {
    setMsg(null); setRows([]); setClientErrors([]); setServerResult(null); setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: normalizeHeader,
      complete: (res) => {
        const seenIds    = new Set();
        const seenEmails = new Set();
        const validRows  = [];
        const errorRows  = [];

        res.data.forEach((raw, idx) => {
          // skip the format-hints row (heuristic: "YYYY-MM-DD" style cells)
          const rowNum = idx + 2; // header is row 1
          const cleaned = Object.fromEntries(
            Object.entries(raw).map(([k, v]) => [k, String(v ?? "").trim()])
          );
          // detect hint row
          if (cleaned.date_of_birth === "YYYY-MM-DD" || cleaned.role === "staff/manager/admin/executive") {
            return;
          }
          // skip wholly-empty rows
          const allEmpty = Object.values(cleaned).every((v) => v === "");
          if (allEmpty) return;

          const errs = validateRow(cleaned, rowNum, seenIds, seenEmails);
          if (errs.length) {
            errorRows.push({
              row: rowNum,
              employee_id: cleaned.employee_id || null,
              email: cleaned.email || null,
              errors: errs,
            });
          } else {
            validRows.push({ ...cleaned, _row: rowNum });
          }
        });

        setRows(validRows);
        setClientErrors(errorRows);
      },
      error: () => setMsg({ type: "error", text: "Failed to read CSV." }),
    });
  };

  const submit = async () => {
    setMsg(null);
    if (!rows.length) {
      setMsg({ type: "error", text: "No valid rows to upload." });
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        users: rows.map((r) => {
          // strip the _row helper before sending
          const { _row, ...rest } = r;
          return rest;
        }),
      };
      const res = await fetch(`${API_BASE}/api/admin/users/bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setServerResult(data);
      const s = data?.summary || {};
      setMsg({
        type: (s.errors || 0) > 0 ? "warn" : "success",
        text: `Done — ${s.created || 0} created, ${s.updated || 0} updated, ${s.skipped || 0} skipped.`,
      });
      onUploaded?.();
    } catch (e) {
      setMsg({ type: "error", text: e.message });
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = () => {
    const allErrors = [
      ...clientErrors.map((e) => ({
        source: "client",
        row: e.row,
        employee_id: e.employee_id || "",
        email: e.email || "",
        status: "skipped (client-validated)",
        errors: e.errors.map((x) => `${x.field}: ${x.error}`).join(" | "),
      })),
      ...((serverResult?.results || [])
        .filter((r) => (r.errors || []).length > 0)
        .map((r) => ({
          source: "server",
          row: r.row,
          employee_id: r.employee_id || "",
          email: r.email || "",
          status: r.status,
          errors: r.errors.map((x) => `${x.field}: ${x.error}`).join(" | "),
        }))),
    ];
    if (!allErrors.length) {
      setMsg({ type: "error", text: "No errors to export." });
      return;
    }
    downloadCsv("bulk_upload_errors.csv", Papa.unparse(allErrors));
  };

  const summary  = serverResult?.summary || null;
  const totalErrors = clientErrors.length + (summary?.errors || 0);

  return (
    <div className="space-y-5">

      {/* Hint banner */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
        <p>
          Download the template, fill in your data, then upload here.
        </p>
        <p className="mt-1 text-xs text-white/50">
          Required fields: <code className="text-white/70">{REQUIRED.join(", ")}</code>.
          Template includes {TEMPLATE_HEADERS.length} columns covering personal info, work details, compensation, and emergency contact.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={downloadTemplate}
          className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-colors">
          <Download size={13} /> Download Template ({TEMPLATE_HEADERS.length} columns)
        </button>
        <label className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors cursor-pointer">
          <Upload size={13} /> Select CSV
          <input type="file" accept=".csv,text/csv" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} className="hidden" />
        </label>
        {rows.length > 0 && (
          <button onClick={submit} disabled={uploading}
            className="flex items-center gap-1.5 text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            <Upload size={13} /> {uploading ? `Uploading ${rows.length} users…` : `Upload ${rows.length} valid row${rows.length !== 1 ? "s" : ""}`}
          </button>
        )}
        {fileName && <span className="text-xs text-white/30">📎 {fileName}</span>}
      </div>

      {/* Status message */}
      {msg && (
        <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${
          msg.type === "error"  ? "border-red-500/30 bg-red-500/10 text-red-300"
          : msg.type === "warn" ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-300"
          :                       "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
        }`}>
          {msg.type === "error" || msg.type === "warn" ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
          {msg.text}
        </div>
      )}

      {/* Server-side summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={`${card} p-4 text-center`}>
            <div className="text-2xl font-bold text-white">{summary.total_rows}</div>
            <div className="text-xs text-white/50 mt-1">Total Rows</div>
          </div>
          <div className={`${card} p-4 text-center border-[#00ff90]/40`}>
            <div className="text-2xl font-bold text-[#00ff90]">{summary.created}</div>
            <div className="text-xs text-white/50 mt-1">Created</div>
          </div>
          <div className={`${card} p-4 text-center border-blue-400/40`}>
            <div className="text-2xl font-bold text-blue-400">{summary.updated}</div>
            <div className="text-xs text-white/50 mt-1">Updated</div>
          </div>
          <div className={`${card} p-4 text-center ${summary.errors > 0 ? "border-red-500/40" : ""}`}>
            <div className={`text-2xl font-bold ${summary.errors > 0 ? "text-red-400" : "text-white/40"}`}>
              {summary.skipped}
            </div>
            <div className="text-xs text-white/50 mt-1">Skipped/Errors</div>
          </div>
        </div>
      )}

      {/* Errors table — combined client-side + server-side */}
      {totalErrors > 0 && (
        <div className={card}>
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <span className="text-xs text-red-400 uppercase tracking-widest font-medium">
              {totalErrors} error{totalErrors !== 1 ? "s" : ""}
            </span>
            <button onClick={downloadErrorReport}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors">
              <FileDown size={12} /> Download error report
            </button>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0d0d0d]">
                <tr className="border-b border-white/5">
                  <th className="px-3 py-2 text-left text-red-400/70 uppercase tracking-wider font-medium">Row</th>
                  <th className="px-3 py-2 text-left text-red-400/70 uppercase tracking-wider font-medium">Employee ID</th>
                  <th className="px-3 py-2 text-left text-red-400/70 uppercase tracking-wider font-medium">Email</th>
                  <th className="px-3 py-2 text-left text-red-400/70 uppercase tracking-wider font-medium">Status</th>
                  <th className="px-3 py-2 text-left text-red-400/70 uppercase tracking-wider font-medium">Errors</th>
                </tr>
              </thead>
              <tbody>
                {clientErrors.map((e, i) => (
                  <tr key={`c-${i}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-3 py-2 text-white/50">{e.row}</td>
                    <td className="px-3 py-2 text-white/50">{e.employee_id || "—"}</td>
                    <td className="px-3 py-2 text-white/50">{e.email || "—"}</td>
                    <td className="px-3 py-2 text-yellow-300">skipped (client)</td>
                    <td className="px-3 py-2 text-red-300/80">
                      {e.errors.map((x, j) => (
                        <div key={j}>• <span className="text-white/40">{x.field}:</span> {x.error}</div>
                      ))}
                    </td>
                  </tr>
                ))}
                {(serverResult?.results || [])
                  .filter((r) => (r.errors || []).length > 0)
                  .map((r, i) => (
                    <tr key={`s-${i}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="px-3 py-2 text-white/50">{r.row}</td>
                      <td className="px-3 py-2 text-white/50">{r.employee_id || "—"}</td>
                      <td className="px-3 py-2 text-white/50">{r.email || "—"}</td>
                      <td className={`px-3 py-2 ${r.status === "error" ? "text-red-400" : "text-yellow-300"}`}>
                        {r.status}
                      </td>
                      <td className="px-3 py-2 text-red-300/80">
                        {(r.errors || []).map((x, j) => (
                          <div key={j}>• <span className="text-white/40">{x.field}:</span> {x.error}</div>
                        ))}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview table — valid rows */}
      {rows.length > 0 && !serverResult && (
        <div className={card}>
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Preview (valid rows)</span>
            <span className="text-xs text-white/30">
              {rows.length} row{rows.length !== 1 ? "s" : ""} ready · {clientErrors.length} skipped client-side
            </span>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0d0d0d]">
                <tr className="border-b border-white/5">
                  {REQUIRED.map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">{h}</th>
                  ))}
                  <th className="px-3 py-2 text-left text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">title</th>
                  <th className="px-3 py-2 text-left text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">department</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
                    {REQUIRED.map(h => (
                      <td key={h} className="px-3 py-2 text-white/50 whitespace-nowrap">{r[h] || "—"}</td>
                    ))}
                    <td className="px-3 py-2 text-white/50 whitespace-nowrap">{r.title || "—"}</td>
                    <td className="px-3 py-2 text-white/50 whitespace-nowrap">{r.department || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 200 && (
              <p className="px-5 py-2 text-white/30 text-xs">Showing first 200 of {rows.length} rows.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
